import React, { useState, useEffect } from 'react';
import { getAnalytics } from 'firebase/analytics';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
`;

const Title = styled.h1`
  color: #1d1d1f;
  margin-bottom: 2rem;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: #f5f5f7;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #007AFF;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #6e6e73;
  font-size: 0.9rem;
`;

const Instructions = styled.div`
  background: #f0f8ff;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #007AFF20;
`;

const Analytics = () => {
  const [stats, setStats] = useState({
    totalRedirects: 0,
    todayRedirects: 0,
    tkRouteClicks: 0,
    pinterestClicks: 0,
    recentClicks: []
  });

  useEffect(() => {
    // Симуляция данных для демонстрации
    // В реальном приложении здесь был бы запрос к Firebase Analytics API
    const simulatedStats = {
      totalRedirects: Math.floor(Math.random() * 1000) + 500,
      todayRedirects: Math.floor(Math.random() * 50) + 10,
      tkRouteClicks: Math.floor(Math.random() * 200) + 50,
      pinterestClicks: Math.floor(Math.random() * 150) + 25,
      recentClicks: [
        { time: '1 минуту назад', route: '/pinterest', userAgent: 'iPhone Safari', destination: 'Pinterest' },
        { time: '3 минуты назад', route: '/tk', userAgent: 'Chrome Desktop', destination: 'App Store' },
        { time: '7 минут назад', route: '/pin', userAgent: 'Android Chrome', destination: 'Pinterest' },
        { time: '12 минут назад', route: '/tk', userAgent: 'iPhone Safari', destination: 'App Store' },
        { time: '18 минут назад', route: '/pinterest', userAgent: 'Firefox Desktop', destination: 'Pinterest' },
      ]
    };
    
    setStats(simulatedStats);
  }, []);

  return (
    <Container>
      <Title>📊 Analytics Dashboard</Title>
      
      <Card>
        <h2>Статистика редиректов</h2>
        <StatGrid>
          <StatCard>
            <StatNumber>{stats.totalRedirects}</StatNumber>
            <StatLabel>Всего редиректов</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatNumber>{stats.todayRedirects}</StatNumber>
            <StatLabel>Сегодня</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatNumber>{stats.tkRouteClicks}</StatNumber>
            <StatLabel>/tk → App Store</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatNumber>{stats.pinterestClicks}</StatNumber>
            <StatLabel>/pinterest → Pinterest</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatNumber>{Math.floor(stats.tkRouteClicks / stats.totalRedirects * 100)}%</StatNumber>
            <StatLabel>App Store конверсия</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatNumber>{Math.floor(stats.pinterestClicks / stats.totalRedirects * 100)}%</StatNumber>
            <StatLabel>Pinterest конверсия</StatLabel>
          </StatCard>
        </StatGrid>
      </Card>

      <Card>
        <h2>Последние переходы</h2>
        {stats.recentClicks.map((click, index) => (
          <div key={index} style={{
            padding: '1rem',
            borderBottom: index < stats.recentClicks.length - 1 ? '1px solid #f0f0f0' : 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <strong>{click.route}</strong> → <span style={{ 
                color: '#007AFF',
                fontWeight: '600'
              }}>App Store</span>
              <span style={{ 
                color: click.route.includes('pinterest') || click.route.includes('pin') ? '#E60023' : '#007AFF',
                fontSize: '0.8rem',
                marginLeft: '8px',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: click.route.includes('pinterest') || click.route.includes('pin') ? '#E6002320' : '#007AFF20'
              }}>
                {click.route.includes('pinterest') || click.route.includes('pin') ? 'Pinterest Route' : 'TK Route'}
              </span>
              <div style={{ fontSize: '0.8rem', color: '#6e6e73' }}>{click.userAgent}</div>
            </div>
            <span style={{ color: '#6e6e73' }}>{click.time}</span>
          </div>
        ))}
      </Card>

      <Instructions>
        <h3>🔍 Где смотреть полную аналитику:</h3>
        
        <h4>📊 Google Analytics Events:</h4>
        <ul>
          <li><strong>TK Route:</strong> TK_ROUTE_CLICK, TK_CONVERSION, TK_COUNT</li>
          <li><strong>Pinterest Route:</strong> PINTEREST_ROUTE_CLICK, PINTEREST_CONVERSION, PINTEREST_COUNT</li>
          <li><strong>Categories:</strong> TK_MARKETING, PINTEREST_MARKETING, CONVERSIONS, COUNTERS</li>
        </ul>
        
        <h4>🔥 Firebase Analytics Events:</h4>
        <ul>
          <li><strong>TK Route:</strong> TK_ROUTE_APP_STORE_REDIRECT, TK_CLICK_COUNT</li>
          <li><strong>Pinterest Route:</strong> PINTEREST_ROUTE_APP_STORE_REDIRECT, PINTEREST_CLICK_COUNT</li>
        </ul>
        
        <h4>🏷️ Google Tag Manager Events:</h4>
        <ul>
          <li><strong>TK Route:</strong> TK_ROUTE_REDIRECT, TK_CLICK_COUNTER</li>
          <li><strong>Pinterest Route:</strong> PINTEREST_ROUTE_REDIRECT, PINTEREST_CLICK_COUNTER</li>
        </ul>
        
        <h3>📱 App Store ссылки:</h3>
        <ul>
          <li><a href="/tk" target="_blank">yoursite.com/tk</a> → App Store</li>
          <li><a href="/download" target="_blank">yoursite.com/download</a> → App Store</li>
          <li><a href="/app" target="_blank">yoursite.com/app</a> → App Store</li>
        </ul>
        
        <h3>📌 Pinterest роуты (тоже ведут в App Store):</h3>
        <ul>
          <li><a href="/pinterest" target="_blank">yoursite.com/pinterest</a> → App Store</li>
          <li><a href="/pin" target="_blank">yoursite.com/pin</a> → App Store</li>
        </ul>
      </Instructions>
    </Container>
  );
};

export default Analytics;